import { NotificationEntity } from '../services/patientNotificationService';

export type FieldDataType = 'text' | 'enum' | 'date' | 'number' | 'dynamic_enum' | 'exists';

export interface FieldMeta {
  key: string;
  label: string;
  dataType: FieldDataType;
  enumOptions?: { value: string; label: string }[];
  dynamicSource?: string;
}

const PATIENT_FIELDS: FieldMeta[] = [
  { key: 'name', label: 'Name', dataType: 'text' },
  { key: 'email', label: 'Email', dataType: 'text' },
  { key: 'phone', label: 'Phone', dataType: 'text' },
  { key: 'hmId', label: 'HM ID', dataType: 'text' },
  { key: 'notes', label: 'Notes', dataType: 'text' },
  { key: 'status', label: 'Status', dataType: 'dynamic_enum', dynamicSource: 'patientStatuses' },
  {
    key: 'titrationStatus',
    label: 'Titration Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'waiting_for_gp_details', label: 'Waiting for GP Details' },
      { value: 'waiting_for_payment', label: 'Waiting for Payment' },
      { value: 'waiting_for_measurements', label: 'Waiting for Measurements' },
      { value: 'measurement_clinical_review_required', label: 'Measurement Clinical Review Required' },
    ],
  },
  {
    key: 'titrationMeasurementStatus',
    label: 'Titration Measurement Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'valid', label: 'Valid' },
      { value: 'not_valid', label: 'Not Valid' },
    ],
  },
  {
    key: 'titrationMeasurementApprovalStatus',
    label: 'Titration Measurement Approval Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'open', label: 'Open' },
      { value: 'approved', label: 'Approved' },
      { value: 'reject', label: 'Rejected' },
      { value: 'request_additional_info', label: 'Request Additional Info' },
    ],
  },
  {
    key: 'medicationMonitoringStatus',
    label: 'Medication Monitoring Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'open', label: 'Open' },
      { value: 'done', label: 'Done' },
    ],
  },
  {
    key: 'gender',
    label: 'Gender',
    dataType: 'enum',
    enumOptions: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' },
      { value: 'Prefer not to say', label: 'Prefer not to say' },
    ],
  },
  { key: 'age', label: 'Age', dataType: 'number' },
  { key: 'clinic', label: 'Clinic', dataType: 'dynamic_enum', dynamicSource: 'clinics' },
  { key: 'nextMeetingTypeId', label: 'Next Meeting Type', dataType: 'dynamic_enum', dynamicSource: 'meetingTypes' },
  { key: 'lastMeeting', label: 'Last Meeting', dataType: 'date' },
  { key: 'nextMeeting', label: 'Next Meeting', dataType: 'exists' },
  // The next meeting's date, as distinct from whether one is booked. For an
  // unbooked titration follow-up it is the clinician's planned date, so a
  // notification can start from it rather than firing straight away.
  { key: 'nextMeetingStart', label: 'Next Meeting Date', dataType: 'date' },
  { key: 'scheduleMeetingStart', label: 'Schedule Meeting Start', dataType: 'date' },
  { key: 'startMedication', label: 'Start Medication', dataType: 'date' },

  { key: 'createdAt', label: 'Created', dataType: 'date' },
  { key: 'updatedAt', label: 'Updated', dataType: 'date' },
];

const MEETING_FIELDS: FieldMeta[] = [
  {
    key: 'status',
    label: 'Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'active', label: 'Active' },
      { value: 'noshow', label: 'No Show' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'done', label: 'Done' },
    ],
  },
  { key: 'type', label: 'Type', dataType: 'dynamic_enum', dynamicSource: 'meetingTypes' },
  { key: 'therapist', label: 'Therapist', dataType: 'dynamic_enum', dynamicSource: 'users' },
  { key: 'date', label: 'Date', dataType: 'date' },
  {
    key: 'summaryApproved',
    label: 'Summary Approved',
    dataType: 'enum',
    enumOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    key: 'summarySent',
    label: 'Summary Sent',
    dataType: 'enum',
    enumOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
];

const QUESTIONNAIRE_FIELDS: FieldMeta[] = [
  {
    key: 'status',
    label: 'Status',
    dataType: 'enum',
    enumOptions: [
      { value: 'SENT', label: 'Sent' },
      { value: 'START', label: 'Started' },
      { value: 'SUBMITTED', label: 'Submitted' },
      { value: 'FINISHED', label: 'Finished' },
      { value: 'EXIT', label: 'Exited' },
    ],
  },
  { key: 'type', label: 'Type', dataType: 'dynamic_enum', dynamicSource: 'questionnaireTypes' },
  { key: 'sentDate', label: 'Sent Date', dataType: 'date' },
  { key: 'startDate', label: 'Start Date', dataType: 'date' },
  { key: 'finishDate', label: 'Finish Date', dataType: 'date' },
  { key: 'createdAt', label: 'Created', dataType: 'date' },
];

export const ENTITY_FIELD_META: Record<NotificationEntity, FieldMeta[]> = {
  [NotificationEntity.PATIENT]: PATIENT_FIELDS,
  [NotificationEntity.MEETING]: MEETING_FIELDS,
  [NotificationEntity.QUESTIONNAIRE]: QUESTIONNAIRE_FIELDS,
};

export function getFieldMeta(entity: NotificationEntity, fieldKey: string): FieldMeta | undefined {
  return ENTITY_FIELD_META[entity]?.find((f) => f.key === fieldKey);
}
